import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Calendar, Clock, Users, Video, MapPin, 
  Loader2, Check, ChevronLeft, ChevronRight,
  RefreshCw, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MeetingBookingModal = ({ 
  isOpen, 
  onClose, 
  email = null,  // Pre-fill from email context
  recipient = null,  // Direct recipient
  onBooked
}: { isOpen: any; onClose: any; email?: any; recipient?: any; onBooked?: any }) => {
  // Form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attendeeEmail, setAttendeeEmail] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [duration, setDuration] = useState(30);
  const [isTeamsMeeting, setIsTeamsMeeting] = useState(true);
  const [location, setLocation] = useState('');
  
  // Data state
  const [availableSlots, setAvailableSlots] = useState([]);
  const [busyTimes, setBusyTimes] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Pre-fill from email
  useEffect(() => {
    if (email) {
      const fromEmail = email.from?.emailAddress?.address || email.from?.email || '';
      const fromName = email.from?.emailAddress?.name || email.from?.name || '';
      
      setAttendeeEmail(fromEmail);
      setSubject(`Møte: ${email.subject || 'Oppfølging'}`);
      setDescription(`Møte angående e-post: "${email.subject}"`);
    }
    
    if (recipient) {
      setAttendeeEmail(recipient.email || recipient);
    }
  }, [email, recipient]);
  
  // Fetch availability when date changes
  useEffect(() => {
    if (isOpen && selectedDate) {
      fetchAvailability();
    }
  }, [isOpen, selectedDate, duration]);
  
  const fetchAvailability = async () => {
    setLoadingSlots(true);
    try {
      const token = auth.getToken();
      
      // Get start and end of selected date
      const start = new Date(selectedDate);
      start.setHours(8, 0, 0, 0);
      
      const end = new Date(selectedDate);
      end.setHours(18, 0, 0, 0);
      
      const response = await axios.get(`${API}/outlook/calendar/availability`, {
        params: {
          start: start.toISOString(),
          end: end.toISOString(),
          duration: duration
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAvailableSlots(response.data.available_slots || []);
      setBusyTimes(response.data.busy_times || []);
      
    } catch (error) {
      console.error('Error fetching availability:', error);
      if (error.response?.status === 401) {
        toast.error('Outlook ikke tilkoblet. Koble til Outlook først.');
      } else {
        toast.error('Kunne ikke hente ledige tider');
      }
    } finally {
      setLoadingSlots(false);
    }
  };
  
  const handleCreateMeeting = async () => {
    if (!attendeeEmail.trim()) {
      toast.error('Vennligst oppgi deltaker');
      return;
    }
    
    if (!selectedSlot) {
      toast.error('Vennligst velg et tidspunkt');
      return;
    }
    
    setCreating(true);
    try {
      const token = auth.getToken();
      
      const startTime = new Date(selectedSlot.start);
      const endTime = new Date(selectedSlot.end);
      
      const response = await axios.post(
        `${API}/outlook/meeting`,
        {
          subject: subject || 'Møte',
          body: description || undefined,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          attendees: [{ email: attendeeEmail.trim() }],
          is_online_meeting: isTeamsMeeting,
          location: !isTeamsMeeting && location ? location : undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Møte opprettet og innkalling sendt!');
        
        if (response.data.teams_link) {
          toast.success(
            <div>
              <p>Teams-lenke opprettet</p>
              <a 
                href={response.data.teams_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-violet-600 underline text-sm"
              >
                Åpne Teams-møte
              </a>
            </div>,
            { duration: 10000 }
          );
        }
        
        onClose();
      }
      
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette møte');
    } finally {
      setCreating(false);
    }
  };
  
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatDate = (date) => {
    return date.toLocaleDateString('nb-NO', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };
  
  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
    setSelectedSlot(null);
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Book møte</h2>
                <p className="text-xs text-gray-500">Med Teams-videolenke</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Form */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Møtetittel
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="F.eks. Oppfølgingsmøte"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            
            {/* Attendee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Users className="w-4 h-4 inline mr-1.5" />
                Deltaker
              </label>
              <input
                type="email"
                value={attendeeEmail}
                onChange={(e) => setAttendeeEmail(e.target.value)}
                placeholder="deltaker@eksempel.no"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            
            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Clock className="w-4 h-4 inline mr-1.5" />
                Varighet
              </label>
              <div className="flex gap-2">
                {[15, 30, 45, 60, 90].map(mins => (
                  <button
                    key={mins}
                    onClick={() => setDuration(mins)}
                    className={cn(
                      "px-4 py-2 text-sm rounded-lg transition-colors",
                      duration === mins
                        ? "bg-emerald-100 text-emerald-700 font-medium"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    )}
                  >
                    {mins} min
                  </button>
                ))}
              </div>
            </div>
            
            {/* Date selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Velg dato
              </label>
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <button
                  onClick={() => changeDate(-1)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div className="text-center">
                  <p className="font-medium text-gray-900 capitalize">
                    {formatDate(selectedDate)}
                  </p>
                </div>
                <button
                  onClick={() => changeDate(1)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Time slots */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Ledige tider
                </label>
                <button
                  onClick={fetchAvailability}
                  disabled={loadingSlots}
                  className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                >
                  <RefreshCw className={cn("w-3 h-3", loadingSlots && "animate-spin")} />
                  Oppdater
                </button>
              </div>
              
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                  <span className="ml-2 text-sm text-gray-500">Henter ledige tider...</span>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  Ingen ledige tider denne dagen
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {availableSlots.map((slot, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedSlot(slot)}
                      className={cn(
                        "px-3 py-2 text-sm rounded-lg transition-all",
                        selectedSlot === slot
                          ? "bg-emerald-500 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-emerald-100"
                      )}
                    >
                      {formatTime(slot.start)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Meeting type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Møtetype
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsTeamsMeeting(true)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all",
                    isTeamsMeeting
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  )}
                >
                  <Video className="w-5 h-5" />
                  Teams-møte
                  {isTeamsMeeting && <Check className="w-4 h-4 ml-auto" />}
                </button>
                <button
                  onClick={() => setIsTeamsMeeting(false)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all",
                    !isTeamsMeeting
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  )}
                >
                  <MapPin className="w-5 h-5" />
                  Fysisk møte
                  {!isTeamsMeeting && <Check className="w-4 h-4 ml-auto" />}
                </button>
              </div>
              
              {!isTeamsMeeting && (
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Sted (f.eks. Kontoret, Møterom A)"
                  className="w-full mt-3 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              )}
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Beskrivelse (valgfritt)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Legg til en beskrivelse eller agenda..."
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              />
            </div>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {selectedSlot && (
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  {formatTime(selectedSlot.start)} - {formatTime(selectedSlot.end)}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={creating}
              >
                Avbryt
              </Button>
              <Button
                onClick={handleCreateMeeting}
                disabled={creating || !selectedSlot || !attendeeEmail.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 min-w-[140px]"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Oppretter...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4" />
                    Opprett møte
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MeetingBookingModal;
